import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chip } from "primereact/chip";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";

const EnachResponse = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);
  const [displayData, setDisplayData] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const encodedData = params.get("data");

    if (!encodedData) {
      setError("No response data received from server");
      setLoading(false);
      return;
    }

    try {
      const decodedData = decodeURIComponent(encodedData);
      const data = JSON.parse(decodedData);

      setResponseData(data);

      const tableData = [];
      if (data.TxnId) tableData.push({ field: "Transaction ID", value: data.TxnId });
      if (data.Status) tableData.push({ field: "Status", value: data.Status });
      if (data.Code) tableData.push({ field: "Code", value: data.Code });
      if (data.Raw) tableData.push({ field: "Raw Message", value: data.Raw, highlight: true });

      setDisplayData(tableData);
    } catch (err) {
      setError(`Failed to parse response data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const getStatusColor = () => {
    if (!responseData) return "#6c757d";
    switch (responseData.Status) {
      case "SUCCESS":
        return "#28a745";
      case "PENDING":
        return "#17a2b8";
      case "FAILED":
        return "#dc3545";
      default:
        return "#ffc107";
    }
  };

  const getStatusText = () => {
    if (!responseData) return "UNKNOWN";
    return responseData.Status;
  };

  const downloadDetails = () => {
    if (!responseData) return;
    const dataStr = JSON.stringify(responseData, null, 2);
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileName = `mandate-response-${responseData.TxnId || Date.now()}.json`;

    const link = document.createElement("a");
    link.href = dataUri;
    link.download = exportFileName;
    link.click();
  };

  const statusTemplate = (rowData) => {
    if (rowData.highlight) {
      return (
        <div className="p-d-flex p-ai-center">
          <span style={{ fontFamily: "monospace", fontWeight: "bold" }}>{rowData.value}</span>
          <Tooltip target=".copy-icon" />
          <i
            className="pi pi-copy copy-icon p-ml-2"
            style={{ cursor: "pointer", color: "#3498db" }}
            data-pr-tooltip="Copy"
            onClick={() => navigator.clipboard.writeText(rowData.value)}
          />
        </div>
      );
    }
    return rowData.value;
  };

  if (loading) {
    return (
      <div className="p-d-flex p-jc-center p-ai-center" style={{ height: "100vh" }}>
        <div className="p-text-center">
          <ProgressSpinner />
          <h2 className="p-mt-3">Processing Worldline Response...</h2>
          <p className="p-text-secondary">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error && !responseData) {
    return (
      <div className="p-p-4" style={{ maxWidth: 800, margin: "0 auto" }}>
        <Card className="p-p-4" style={{ background: "#ffcccc" }}>
          <h2>Error Loading Response</h2>
          <p>{error}</p>
          <Button label="Return Home" onClick={() => navigate("/")} />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-p-4" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <Card className="p-mb-4 p-shadow-4">
        <div className="p-d-flex p-ai-center">
          <i
            className="pi pi-info-circle"
            style={{
              fontSize: "4rem",
              color: getStatusColor(),
              marginRight: "1rem"
            }}
          />

          <div>
            <h1 className="p-mt-0 p-mb-2">eNACH Mandate Registration Result</h1>

            <Tag value={getStatusText()} severity={responseData.Status === "FAILED" ? "danger" : "success"} style={{ fontSize: "1rem" }} />

            <p style={{ marginTop: 10 }}>{responseData.Code}</p>
          </div>
        </div>
      </Card>

      <Card title="Transaction Details" className="p-mb-4">
        <DataTable value={displayData} size="small" responsiveLayout="scroll" showGridlines>
          <Column field="field" header="Field" style={{ width: "30%", fontWeight: "bold" }} />
          <Column field="value" header="Value" body={statusTemplate} />
        </DataTable>
      </Card>

      <div className="p-d-flex p-gap-3 p-mb-5">
        <Button label="Back to Home" icon="pi pi-home" onClick={() => navigate("/")} />
        <Button label="Download Details" icon="pi pi-download" onClick={downloadDetails} />
        <Button label="View Raw Data" icon="pi pi-code" className="p-button-outlined" onClick={() => setVisible(true)} />
      </div>

      <Dialog header="Raw Response Data" visible={visible} style={{ width: "80vw" }} maximizable onHide={() => setVisible(false)}>
        <pre style={{ background: "#f8f9fa", padding: "1rem" }}>{JSON.stringify(responseData, null, 2)}</pre>
      </Dialog>

      <Divider />
      <div className="p-text-center p-text-secondary p-mt-4">
        <p>Powered by Worldline India | NPCI Certified</p>
      </div>
    </div>
  );
};

export default EnachResponse;
